import { useState } from "react";
import { ChevronLeftIcon } from "../shared/components/icons";
import type { Channel } from "../shared/host";
import { SkillsTab } from "./tabs/SkillsTab";
import { ConnectionsTab } from "./tabs/ConnectionsTab";
import { ModulesTab } from "./tabs/ModulesTab";
import { AccountTab } from "./tabs/AccountTab";
import styles from "./SettingsPage.module.css";

export type SettingsTabId = "skills" | "connections" | "modules" | "account";

interface SettingsPageProps {
  readonly onBack: () => void;
  readonly initialTab?: SettingsTabId;
  /** Active Office channel — keys the per-host user skill preferences. */
  readonly host: Channel | null;
  readonly userEmail: string;
  readonly darkMode: boolean;
  readonly onToggleDarkMode: (next: boolean) => void;
  readonly onSignOut: () => void;
}

const TABS: ReadonlyArray<{ id: SettingsTabId; label: string }> = [
  { id: "skills", label: "Skills" },
  { id: "connections", label: "Connections" },
  { id: "modules", label: "Modules" },
  { id: "account", label: "Account" },
];

export function SettingsPage({
  onBack,
  initialTab = "skills",
  host,
  userEmail,
  darkMode,
  onToggleDarkMode,
  onSignOut,
}: Readonly<SettingsPageProps>) {
  const [activeTab, setActiveTab] = useState<SettingsTabId>(initialTab);

  return (
    <div className={styles.page}>
      <button type="button" className={styles.back} onClick={onBack} aria-label="Back">
        <ChevronLeftIcon />
        Settings
      </button>

      <div className={styles.tabBar} role="tablist" aria-label="Settings sections">
        {TABS.map((t) => {
          const active = t.id === activeTab;
          return (
            <button
              key={t.id}
              type="button"
              role="tab"
              aria-selected={active}
              tabIndex={active ? 0 : -1}
              className={`${styles.tab} ${active ? styles.tabActive : ""}`}
              onClick={() => setActiveTab(t.id)}
            >
              {t.label}
            </button>
          );
        })}
      </div>

      <div className={styles.panels}>
        {activeTab === "skills" && <SkillsTab host={host} />}
        {activeTab === "connections" && <ConnectionsTab />}
        {activeTab === "modules" && <ModulesTab host={host} />}
        {activeTab === "account" && (
          <AccountTab
            userEmail={userEmail}
            darkMode={darkMode}
            onSignOut={onSignOut}
            onToggleDarkMode={onToggleDarkMode}
          />
        )}
      </div>
    </div>
  );
}
